import dayjs from "dayjs";

import { AD_LAYOUT_STUBS, AD_PLACEMENTS } from "@consts";
import {
	getRandomItemsFromArray,
	isEmpty,
	canonical,
	openExternalLinkInNewTab,
	forceHistoryPushOnAnchorElement,
	isInternalAnchorElement,
	findAnchorElementFromEvent,
} from "@utils";
import SocialSharing from "./SocialSharing.vue";
import { getArticleData, getArticles } from "@/common/services/articleService";
import { modifyWithNewsletterPlaceholderDiv } from "~/pages/blog/BlogArticle/helpers";
import NewsletterBanner from "@/components/NewsletterBanner/NewsletterBanner.vue";

export default {
	name: "BlogArticle",
	components: {
		SocialSharing,
		NewsletterBanner,
	},
	async asyncData({ $axios, $config, params, query, store, error }) {
		const articleResponse = await getArticleData(
			$axios,
			$config,
			params.slug,
			query.preview,
			store
		);

		if (articleResponse.status === 404) {
			error({ statusCode: 404 });
			return;
		}

		let articlesList;
		if (!store.getters["navigation/isWordleArticleDetailPage"]) {
			const articlesListResponse = await getArticles($axios);

			articlesList = articlesListResponse?.data || [];
			articlesList = getRandomItemsFromArray(
				articlesList.filter((a) => a.id !== articleResponse?.data.id),
				3
			);
		}

		const hideAds = (articleResponse?.data?.flags || []).includes("no-ads");
		store.commit("articles/SET_HIDE_ADS", hideAds);

		return {
			article: articleResponse?.data || {},
			latestArticles: articlesList,
		};
	},
	head() {
		const h = {
			title: this.article.metaTitle,
			meta: [
				{
					name: "description",
					hid: "description",
					content: this.article.metaDescription,
				},
				{
					name: "og:title",
					content: this.article.metaTitle,
				},
				{
					name: "og:description",
					content:
						this.article.teaser || this.article.metaDescription,
				},
				{
					name: "og:image",
					content: this.article?.coverImage?.urlBase || "",
				},
				{
					name: "og:url",
					content: canonical(this.$route).href,
				},
				{
					name: "og:type",
					content: "article",
				},
				{
					name: "article:published_time",
					content: this.entryPublishedTime,
				},
				{
					hid: "max-image-preview",
					name: "robots",
					content: "max-image-preview:large",
				},
			],
			link: [canonical(this.$route)],
		};

		if (this.isWordleArticle) {
			h.script = h.script || [];
			h.script.push({
				hid: "jsonld-wordle-article-schema",
				type: "application/ld+json",
				json: {
					"@context": "https://schema.org",
					"@type": "NewsArticle",
					headline: this.article.metaTitle,
					image: [this.article?.coverImage?.urlBase || ""],
					datePublished: dayjs
						.tz(this.article.firstPublishedAt, "UTC")
						.toISOString(),
					dateModified: dayjs
						.tz(this.article.updatedAt, "UTC")
						.toISOString(),
					author: [
						{
							"@type": "Person",
							name: this.article.author?.displayName || "",
							url: "https://wordfinder.yourdictionary.com/about/",
						},
					],
				},
			});
		}

		return h;
	},
	data() {
		return {
			article: {},
			latestArticles: [],
			AD_LAYOUT_STUBS,
			AD_PLACEMENTS,
		};
	},
	computed: {
		hasArticleData() {
			return !isEmpty(this.article);
		},
		showDate() {
			if ((this.article?.flags || []).includes("hide-date")) {
				return false;
			}

			return true;
		},
		articleDate() {
			return new Date(this.article.updatedAt).toLocaleDateString("en", {
				month: "short",
				day: "numeric",
				year: "numeric",
			});
		},
		articleMeta() {
			const strings = [];
			const articleAuthor = this.article.author.displayName;
			if (articleAuthor) {
				strings.push(articleAuthor);
			}

			if (this.showDate) {
				strings.push(this.articleDate);
			}

			strings.push(
				`${this.article.minutesToRead} ${this.$t(
					"ArticlesIndexPageReadingTime"
				)}`
			);

			return strings.join(" &bull; ");
		},
		articleImage() {
			return this.article.coverImage;
		},
		articleTags() {
			return this.article.tags?.length ? this.article.tags : [];
		},
		entryPublishedTime() {
			const firstPublishedAt = this.article.firstPublishedAt;

			return firstPublishedAt
				? new Date(firstPublishedAt).toLocaleDateString("en-CA")
				: null;
		},
		showAds() {
			return !this.$store.state.articles.hideAds;
		},
		isWordleArticle() {
			return this.$store.getters["navigation/isWordleArticleDetailPage"];
		},
		articleBody() {
			return modifyWithNewsletterPlaceholderDiv(this.article?.body || "");
		},
	},
	methods: {
		onBodyCopyClick(e) {
			const anchorElement = findAnchorElementFromEvent(e);

			if (!anchorElement) {
				return;
			}

			e.preventDefault();

			if (isInternalAnchorElement(anchorElement)) {
				forceHistoryPushOnAnchorElement(
					anchorElement,
					this.$router.history
				);
				return;
			}

			openExternalLinkInNewTab(anchorElement, window);
		},
	},
	destroyed() {
		this.$store.commit("articles/SET_HIDE_ADS", false);
	},
};
